import { Callback } from '../../../types';
import { isMobile } from '../../../utils';
import Video, { ConnectOptions, LocalTrack, Room } from 'twilio-video';
import { VideoRoomMonitor } from '@twilio/video-room-monitor';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppState } from '../../../state';
import dayjs from 'dayjs';
import { REACT_APP_DEFAULT_VIDEO_CALL_TIME, REACT_APP_IS_DEMO_MODE } from '../../../configs';
import useLesson from '../../../hooks/useLesson/useLesson';

// @ts-ignore
window.TwilioVideo = Video;

export default function useRoom(localTracks: LocalTrack[], onError: Callback, options?: ConnectOptions) {
  const { lessonInfo } = useAppState();
  const [room, setRoom] = useState<Room | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const optionsRef = useRef(options);
  const { setVideoEndTime } = useAppState();
  const { lesson, getLessonEndDatetimeExact } = useLesson({
    organization_key: lessonInfo.organization_key,
    roomName: lessonInfo.roomName,
  });

  useEffect(() => {
    // This allows the connect function to always access the most recent version of the options object. This allows us to
    // reliably use the connect function at any time.
    optionsRef.current = options;
  }, [options]);

  const connect = useCallback(
    token => {
      setIsConnecting(true);
      return Video.connect(token, { ...optionsRef.current, tracks: localTracks }).then(
        newRoom => {
          setRoom(newRoom);
          VideoRoomMonitor.registerVideoRoom(newRoom);
          const disconnect = () => newRoom.disconnect();

          // This app can add up to 16 'participantDisconnected' listeners to the room object, which can trigger
          // a warning from the EventEmitter object. Here we increase the max listeners to suppress the warning.
          newRoom.setMaxListeners(16);

          newRoom.once('disconnected', () => {
            // Reset the room only after all other `disconnected` listeners have been called.
            setTimeout(() => setRoom(null));
            window.removeEventListener('beforeunload', disconnect);

            if (isMobile) {
              window.removeEventListener('pagehide', disconnect);
            }
          });

          // @ts-ignore
          window.twilioRoom = newRoom;

          newRoom.localParticipant.videoTracks.forEach(publication =>
            // All video tracks are published with 'low' priority because the video track
            // that is displayed in the 'MainParticipant' component will have it's priority
            // set to 'high' via track.setPriority()
            publication.setPriority('low')
          );

          setIsConnecting(false);

          // Add a listener to disconnect from the room when a user closes their browser
          window.addEventListener('beforeunload', disconnect);

          if (isMobile) {
            // Add a listener to disconnect from the room when a mobile user closes their browser
            window.addEventListener('pagehide', disconnect);
          }

          const endTime = (() => {
            if (!REACT_APP_IS_DEMO_MODE && lesson) {
              const lessonEndTimeFormatted = getLessonEndDatetimeExact();
              return dayjs(lessonEndTimeFormatted);
            } else {
              return dayjs().add(REACT_APP_DEFAULT_VIDEO_CALL_TIME, 'm');
            }
          })();

          setVideoEndTime(endTime);
        },
        error => {
          onError(error);
          setIsConnecting(false);
        }
      );
    },
    [localTracks, onError, lesson, setVideoEndTime, getLessonEndDatetimeExact]
  );

  return { room, isConnecting, connect };
}
